import React, { useState, useEffect } from 'react';
import { controlAPI, ControlRunResp } from '../../services/api';
import { TH, LineChart, FracOutline, PANEL, PANEL_HDR, Series } from './common';

// /control/policy — RL 自适应调控实时演示: 同一地层场景下 RL策略 vs 常规阶梯加砂
export default function ControlOverviewPage() {
  const [data, setData] = useState<ControlRunResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [seed, setSeed] = useState<string>('');

  const run = async (fixedSeed?: number) => {
    setLoading(true); setErr('');
    try {
      const body = fixedSeed != null ? { seed: fixedSeed } : (seed ? { seed: Number(seed) } : {});
      const r = await controlAPI.run(body);
      setData(r.data); setSeed(String(r.data.seed));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || '运行失败, 请确认已训练策略 (train_control)');
    } finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const S = (side: 'rl' | 'baseline', key: keyof ControlRunResp['rl']['trace'], color: string, label: string, dash?: boolean): Series =>
    ({ x: data![side].trace.t, y: data![side].trace[key] as number[], color, label, dash });

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: TH.text }}>自适应调控 · 策略演示</span>
        <span style={{ fontSize: 12, color: TH.textMuted }}>强化学习 (PPO) 实时决策泵注排量 / 砂比 · 对比常规阶梯加砂</span>
      </div>

      {/* 控制条 */}
      <div style={{ padding: '10px 20px', display: 'flex', gap: 14, alignItems: 'center', borderBottom: `1px solid ${TH.headerBorder}`, background: TH.panelHeaderBg, flexWrap: 'wrap' }}>
        <button onClick={() => run()} disabled={loading}
          style={{ padding: '5px 16px', borderRadius: 4, border: 'none', background: TH.rl, color: '#fff', fontSize: 13, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? '推演中…' : '↻ 换一个随机地层场景'}
        </button>
        <label style={{ fontSize: 12, color: TH.textSec }}>指定 seed:
          <input value={seed} onChange={(e) => setSeed(e.target.value)} placeholder="随机"
            style={{ width: 90, marginLeft: 6, fontSize: 12, padding: '3px 6px', borderRadius: 3, border: `1px solid ${TH.headerBorder}` }} />
        </label>
        <button onClick={() => run(Number(seed))} disabled={loading || !seed}
          style={{ padding: '5px 12px', borderRadius: 4, border: `1px solid ${TH.headerBorder}`, background: '#fff', fontSize: 12, cursor: 'pointer' }}>
          用此 seed 复现
        </button>
        {err && <span style={{ color: TH.warn, fontSize: 12 }}>{err}</span>}
      </div>

      {!data ? (
        <div style={{ padding: 40, color: TH.textSec }}>{loading ? '加载中…' : '暂无数据'}</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* 地层场景 + 提升卡片 */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ ...PANEL, flex: '1 1 320px' }}>
              <div style={PANEL_HDR}>地层 / 工况场景 (seed {data.seed})</div>
              <div style={{ padding: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 12, color: TH.textSec, fontFamily: TH.fontMono }}>
                <span>杨氏模量 E: {data.scenario.E_gpa} GPa</span><span>泊松比 ν: {data.scenario.nu}</span>
                <span>缝高 h: {data.scenario.frac_height_m} m</span><span>滤失 C: {data.scenario.leakoff_c}</span>
                <span>液量预算: {data.scenario.fluid_budget_m3} m³</span><span>最大排量: {data.scenario.q_max_m3min} m³/min</span>
                <span>砂比上限: {data.scenario.conc_max_kgm3} kg/m³</span><span>净压上限: {data.scenario.pn_limit_mpa} MPa</span>
              </div>
            </div>
            {[
              { k: 'propped_half_length' as const, label: '支撑半缝长', unit: 'm', rl: data.rl.summary.propped_half_length, bl: data.baseline.summary.propped_half_length, up: data.uplift.propped_half_length },
              { k: 'proppant_kg' as const, label: '铺砂量', unit: 't', rl: data.rl.summary.proppant_kg / 1000, bl: data.baseline.summary.proppant_kg / 1000, up: data.uplift.proppant_kg },
              { k: 'conductivity' as const, label: '导流能力', unit: '', rl: data.rl.summary.conductivity, bl: data.baseline.summary.conductivity, up: data.uplift.conductivity },
            ].map((c) => (
              <div key={c.k} style={{ ...PANEL, flex: '1 1 150px', padding: '8px 12px' }}>
                <div style={{ fontSize: 12, color: TH.textMuted }}>{c.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: TH.rl, fontFamily: TH.fontMono }}>{c.rl.toFixed(1)}<span style={{ fontSize: 12 }}> {c.unit}</span></div>
                <div style={{ fontSize: 11, color: TH.textSec }}>常规 {c.bl.toFixed(1)} · <span style={{ color: c.up >= 0 ? TH.ok : TH.warn, fontWeight: 600 }}>{c.up >= 0 ? '+' : ''}{c.up}%</span></div>
              </div>
            ))}
          </div>

          {/* 砂堵状态提示 */}
          <div style={{ display: 'flex', gap: 12 }}>
            {(['rl', 'baseline'] as const).map((side) => {
              const s = data[side].summary; const isRL = side === 'rl';
              return (
                <div key={side} style={{ ...PANEL, flex: 1, padding: '6px 12px', fontSize: 12, display: 'flex', gap: 16, alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, color: isRL ? TH.rl : TH.base }}>{isRL ? 'RL 自适应调控' : '常规阶梯加砂'}</span>
                  <span style={{ color: s.screenout ? TH.warn : TH.ok, fontWeight: 600 }}>{s.screenout ? '⚠ 发生砂堵' : '✓ 无砂堵'}</span>
                  <span style={{ color: TH.textSec }}>施工 {s.duration_min} min · 液效 {(s.efficiency * 100).toFixed(0)}%</span>
                </div>
              );
            })}
          </div>

          {/* 施工轨迹对比 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
            <div style={PANEL}><LineChart title="砂比决策 (kg/m³)" xlabel="时间 min"
              series={[S('rl', 'conc', TH.rl, 'RL'), S('baseline', 'conc', TH.base, '常规', true)]} /></div>
            <div style={PANEL}><LineChart title="泵注排量 (m³/min)" xlabel="时间 min"
              series={[S('rl', 'rate', TH.rl, 'RL'), S('baseline', 'rate', TH.base, '常规', true)]} /></div>
            <div style={PANEL}><LineChart title="砂堵接近度 (缝内砂浓/临界)" xlabel="时间 min" yRef={{ v: 1, label: '砂堵临界' }}
              series={[S('rl', 'so_ratio', TH.rl, 'RL'), S('baseline', 'so_ratio', TH.base, '常规', true)]} /></div>
            <div style={PANEL}><LineChart title="净压 (MPa)" xlabel="时间 min"
              series={[S('rl', 'pn', TH.rl, 'RL'), S('baseline', 'pn', TH.base, '常规', true)]} /></div>
            <div style={PANEL}><LineChart title="支撑半缝长扩展 (m)" xlabel="时间 min"
              series={[S('rl', 'propped', TH.rl, 'RL'), S('baseline', 'propped', TH.base, '常规', true)]} /></div>
            <div style={PANEL}>
              <div style={PANEL_HDR}>最终缝网轮廓对比 (侧视)</div>
              <FracOutline rl={data.rl.outline} base={data.baseline.outline} />
            </div>
          </div>

          <div style={{ fontSize: 12, color: TH.textMuted, lineHeight: 1.6 }}>
            说明: RL 智能体 (PPO) 每 6 秒观测缝内状态 (净压/缝宽/砂堵接近度/预算消耗等 12 维), 决策下一步排量与砂比;
            目标是在<b style={{ color: TH.text }}>不砂堵、不超压、液量预算内</b>尽可能多地把支撑剂铺进地层。常规阶梯加砂按固定节奏一路上调砂比、不看缝内状态,
            高滤失场景下极易砂堵。RL 学会<b style={{ color: TH.text }}>贴着砂堵边界操作</b>——加砂接近临界时回退, 从而放置远多于常规的支撑剂。
          </div>
        </div>
      )}
    </div>
  );
}
